import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { widgetApi } from "../services/api";
import WidgetCard from "../components/WidgetCard";
import {
  PlusIcon,
  SearchIcon,
  ErrorIcon,
  EmptyIcon,
} from "../components/icons";
import errorTracker from "../utils/errorTracker";
import {
  CIRCUIT_BREAKER_MESSAGES,
  isCircuitBreakerError,
} from "../utils/circuitBreaker";
import { PAGINATION } from "../config/constants";
import "./WidgetsList.css";

// Constants
const ITEMS_PER_PAGE = PAGINATION.WIDGETS_PER_PAGE;
const MAX_CLIENT_SIDE_WIDGETS = PAGINATION.MAX_CLIENT_SIDE_WIDGETS;

const WidgetsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [allWidgets, setAllWidgets] = useState([]); // Store all widgets for client-side filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const gridRef = useRef(null);

  // Fetch all widgets once (client-side pagination/filtering for simplicity)
  // For production with 1000s of widgets, implement server-side filtering
  const fetchWidgets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch a large page to get all widgets for client-side filtering
      // This works well for < 1000 widgets. For more, implement server-side filtering.
      const response = await widgetApi.getWidgets(1, MAX_CLIENT_SIDE_WIDGETS);
      setAllWidgets(response.widgets || []);
    } catch (err) {
      // Handle circuit breaker errors gracefully
      if (isCircuitBreakerError(err)) {
        setError(CIRCUIT_BREAKER_MESSAGES.SERVICE_UNAVAILABLE);
        return;
      }

      // Use standardized error handling
      errorTracker.captureException(err, { context: "fetchWidgets" });

      // Show generic error to user (don't leak backend details)
      setError("Failed to load widgets. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch widgets whenever we navigate to this route
  // This ensures fresh data after creating/editing widgets
  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets, location.pathname]);

  const handleCreateWidget = () => {
    navigate("/widgets/new");
  };

  const handleDeleteWidget = async (widgetId) => {
    try {
      await widgetApi.deleteWidget(widgetId);

      // Remove from local state optimistically
      setAllWidgets((prevWidgets) => {
        const newWidgets = prevWidgets.filter((w) => w.id !== widgetId);

        // Check if we need to reset page after deletion
        // Calculate from newWidgets instead of stale closure
        const newFilteredCount = newWidgets.filter((w) => {
          const matchesSearch =
            !searchTerm ||
            w.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            w.display_name?.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesStatus =
            statusFilter === "all" || w.status === statusFilter;
          return matchesSearch && matchesStatus;
        }).length;

        // Reset to page 1 if current page becomes empty after deletion
        if (newFilteredCount > 0) {
          const newTotalPages = Math.ceil(newFilteredCount / ITEMS_PER_PAGE);
          if (currentPage > newTotalPages) {
            setCurrentPage(newTotalPages);
          }
        } else if (currentPage > 1) {
          setCurrentPage(1);
        }

        return newWidgets;
      });
    } catch (err) {
      errorTracker.captureException(err, { context: "deleteWidget", widgetId });

      // Generic error message (don't leak backend details)
      throw new Error("Failed to delete widget. Please try again.");
    }
  };

  const handleViewAnalytics = (widgetId) => {
    navigate(`/widgets/${widgetId}/analytics`);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages && !loading) {
      setCurrentPage(currentPage + 1);

      // Scroll to top of grid instead of absolute top
      if (gridRef.current) {
        gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && !loading) {
      setCurrentPage(currentPage - 1);

      // Scroll to top of grid instead of absolute top
      if (gridRef.current) {
        gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  // Filter widgets based on search term and status (memoized for performance)
  const filteredWidgets = useMemo(() => {
    return allWidgets.filter((widget) => {
      const matchesSearch =
        !searchTerm ||
        widget.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        widget.display_name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || widget.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [allWidgets, searchTerm, statusFilter]);

  // Paginate filtered results (memoized for performance)
  const { totalCount, totalPages, startIndex, endIndex, paginatedWidgets } =
    useMemo(() => {
      const totalCount = filteredWidgets.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedWidgets = filteredWidgets.slice(startIndex, endIndex);

      return { totalCount, totalPages, startIndex, endIndex, paginatedWidgets };
    }, [filteredWidgets, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  if (loading && allWidgets.length === 0) {
    return (
      <div className="widgets-list-page">
        <div className="widgets-list-loading">
          <div className="widgets-list-spinner"></div>
          <p>Loading widgets...</p>
        </div>
      </div>
    );
  }

  if (error && allWidgets.length === 0) {
    return (
      <div className="widgets-list-page">
        <div className="widgets-list-error">
          <ErrorIcon size={48} />
          <h2>{error}</h2>
          <button onClick={fetchWidgets} className="widgets-list-retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="widgets-list-page">
      <div className="widgets-list-header">
        <div className="widgets-list-header-left">
          <h1 className="widgets-list-title">My Widgets</h1>
          <p className="widgets-list-subtitle">
            Manage your chatbot widgets ({totalCount} total)
          </p>
        </div>
        <button
          onClick={handleCreateWidget}
          className="widgets-list-create-btn"
        >
          <PlusIcon size={16} />
          Create Widget
        </button>
      </div>

      <div className="widgets-list-controls">
        <div className="widgets-list-search">
          <SearchIcon size={16} />
          <input
            type="text"
            placeholder="Search widgets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="widgets-list-search-input"
            aria-label="Search widgets"
          />
        </div>

        <div className="widgets-list-filter">
          <label className="widgets-list-filter-label">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="widgets-list-filter-select"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {paginatedWidgets.length === 0 ? (
        <div className="widgets-list-empty">
          <EmptyIcon size={64} />
          <h2>No widgets found</h2>
          {searchTerm || statusFilter !== "all" ? (
            <>
              <p>Try adjusting your search or filter criteria</p>
              <button
                onClick={clearFilters}
                className="widgets-list-empty-btn widgets-list-empty-btn-secondary"
              >
                Clear Filters
              </button>
            </>
          ) : (
            <>
              <p>Create your first widget to get started</p>
              <button
                onClick={handleCreateWidget}
                className="widgets-list-empty-btn"
              >
                <PlusIcon size={16} />
                Create Your First Widget
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="widgets-list-grid" ref={gridRef}>
            {paginatedWidgets.map((widget) => (
              <WidgetCard
                key={widget.id}
                widget={widget}
                onDelete={handleDeleteWidget}
                onViewAnalytics={handleViewAnalytics}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="widgets-list-pagination">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1 || loading}
                className="widgets-list-pagination-btn"
                aria-label="Previous page"
              >
                Previous
              </button>
              <span className="widgets-list-pagination-info">
                Page {currentPage} of {totalPages}
                {totalCount > 0 && (
                  <span className="widgets-list-pagination-count">
                    {" "}
                    ({startIndex + 1}-{Math.min(endIndex, totalCount)} of{" "}
                    {totalCount})
                  </span>
                )}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages || loading}
                className="widgets-list-pagination-btn"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WidgetsList;
